/**
 * @module FCCart
 * @description Shared cart-add helpers for FitCheck add-to-cart flows. Handles
 * request body preparation, response parsing, failure normalization, and cart
 * count extraction so quick add and PDP forms can share one contract.
 * @requires @fc/utilities
 */
import { fetchConfig } from '@fc/utilities';
import { findHTMLNode, parseHTMLDocument, patchHTML } from '@fc/section-response';
import { buildSectionSelector, updateSection } from '@fc/sections';
import { emitCartCountSync } from '@fc/events';
import { cartPerformance } from '@fc/metrics';

const CART_ITEMS_SELECTOR = 'cart-items';
const CART_DRAWER_ITEMS_SELECTOR = 'cart-drawer cart-items';
const CART_COUNT_SELECTOR = '[data-ref="cartItemCount"], [data-ref="cartBadgeCount"]';
const CART_BUNDLED_SECTION_LIMIT = 5;
const CART_ADD_FALLBACK_ERROR = 'Unable to add this item to your cart.';
const CART_ADD_LIMIT_FALLBACK_ERROR = 'This item is sold out or you already have the available quantity in your cart.';
const CART_ADD_SESSION_ERROR = 'Session expired. Refresh this page and try again.';
const QUANTITY_ADJUSTED_FALLBACK = 'Quantity updated to {{ quantity }} based on current availability.';
const QUANTITY_ADDED_ADJUSTED_FALLBACK = 'Added {{ quantity }} to your cart based on current availability.';
let cartCountSyncSequence = 0;

const asString = (value) => (value == null || value === '' ? null : String(value));
const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

function toPositiveInt(value, fallback = 0) {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function toCount(value) {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function cssEscape(value) {
  if (window.CSS?.escape) return window.CSS.escape(String(value));
  return String(value).replace(/["\\]/g, '\\$&');
}

function getPayloadItemCount(source) {
  if (!isPlainObject(source)) return null;
  return toCount(source.item_count ?? source.itemCount);
}

export function getCartLineQuantity(source, line) {
  if (!isPlainObject(source)) return null;

  const lineNumber = Number.parseInt(String(line ?? ''), 10);
  if (!Number.isFinite(lineNumber) || lineNumber < 1) return null;

  const items = Array.isArray(source.items) ? source.items : null;
  if (!items?.length) return null;

  return toCount(items[lineNumber - 1]?.quantity);
}

function createCartCountSyncOperationId() {
  cartCountSyncSequence += 1;
  return `cart-count-${Date.now()}-${cartCountSyncSequence}`;
}

function cloneFormData(source) {
  if (source instanceof HTMLFormElement) {
    return new FormData(source);
  }

  const body = new FormData();

  if (source instanceof FormData) {
    for (const [key, value] of source.entries()) {
      body.append(key, value);
    }
  }

  return body;
}

function getSourceQuantityValue(source) {
  if (!(source instanceof HTMLFormElement)) return null;

  const fields = Array.from(source.querySelectorAll('[name="quantity"]'));
  const preferredField =
    fields.find(
      (field) =>
        field instanceof HTMLInputElement &&
        field.type !== 'hidden' &&
        field.type !== 'submit' &&
        field.type !== 'button',
    ) ?? fields[0];

  if (
    preferredField instanceof HTMLInputElement ||
    preferredField instanceof HTMLSelectElement ||
    preferredField instanceof HTMLTextAreaElement
  ) {
    return preferredField.value;
  }

  return null;
}

function resolveFitCheckGlobal() {
  const fitCheckGlobal = window.FitCheck ?? (typeof FitCheck !== 'undefined' ? FitCheck : null);

  if (fitCheckGlobal && window.FitCheck !== fitCheckGlobal) {
    window.FitCheck = fitCheckGlobal;
  }

  return fitCheckGlobal;
}

function formatQuantityMessage(template, quantity, fallback) {
  const safeQuantity = String(toPositiveInt(quantity, 0));
  const source = typeof template === 'string' && template.trim() ? template : fallback;

  return source.replace(/\{\{\s*quantity\s*\}\}/g, safeQuantity).replace(/\[quantity\]/g, safeQuantity);
}

export function getQuantityAdjustedMessage(quantity) {
  return formatQuantityMessage(resolveFitCheckGlobal()?.translations?.quantity_adjusted, quantity, QUANTITY_ADJUSTED_FALLBACK);
}

export function getQuantityAddedAdjustedMessage(quantity) {
  return formatQuantityMessage(
    resolveFitCheckGlobal()?.translations?.quantity_added_adjusted,
    quantity,
    QUANTITY_ADDED_ADJUSTED_FALLBACK,
  );
}

function getCartAddLimitErrorMessage() {
  const message = resolveFitCheckGlobal()?.translations?.cart_add_limit_error;
  return typeof message === 'string' && message.trim() ? message : CART_ADD_LIMIT_FALLBACK_ERROR;
}

function isZeroQuantityAddedMessage(value) {
  return /\bonly\s+0\s+items?\s+(?:was|were)\s+added\b/i.test(String(value ?? ''));
}

async function parseCartAddPayload(response) {
  const rawText = await response.text();
  if (!rawText) return { payload: null, rawText: '' };

  try {
    return { payload: JSON.parse(rawText), rawText };
  } catch {
    return { payload: null, rawText };
  }
}

function normalizeCartAddFailure(response, payload, rawText) {
  const status = Number(payload?.status) || response.status || 500;
  const rawDescription = payload?.description || (rawText?.trim() ? rawText : response.statusText || '');
  const useLimitMessage = isZeroQuantityAddedMessage(payload?.message) || isZeroQuantityAddedMessage(rawDescription);
  const limitMessage = getCartAddLimitErrorMessage();
  const message =
    (useLimitMessage && limitMessage) ||
    payload?.message ||
    (status === 401 ? CART_ADD_SESSION_ERROR : `${CART_ADD_FALLBACK_ERROR} (HTTP ${status})`);
  const description = useLimitMessage ? limitMessage : rawDescription;

  return {
    ...(payload && typeof payload === 'object' ? payload : {}),
    status,
    message,
    description,
    errors: payload?.errors || {},
  };
}

function normalizeRuntimeCartAddFailure(error) {
  return {
    status: 500,
    message: CART_ADD_FALLBACK_ERROR,
    description: error instanceof Error ? error.message : 'Unknown error',
    errors: {},
  };
}

function normalizeCartAddNoQuantityFailure(payload) {
  const message = getCartAddLimitErrorMessage();
  const description = message;

  return {
    status: Number(payload?.status) || 422,
    message,
    description,
    errors: payload?.errors || {},
  };
}

export function getCartSections(source) {
  if (!isPlainObject(source)) return null;

  if (isPlainObject(source.sections)) {
    return source.sections;
  }

  const values = Object.values(source);
  if (!values.length) return null;

  return values.every((value) => value == null || typeof value === 'string') ? source : null;
}

export function collectCartSectionIds(root = document) {
  const ids = new Set();

  for (const element of root.querySelectorAll(CART_ITEMS_SELECTOR)) {
    if (!(element instanceof HTMLElement) || !element.dataset.sectionId) continue;
    ids.add(element.dataset.sectionId);
  }

  return [...ids];
}

export function collectCartAddSectionIds(root = document, options = {}) {
  const ids = new Set();
  const sourceElement = options.sourceElement instanceof Element ? options.sourceElement : null;
  const sourceCartItems = sourceElement?.closest?.(`${CART_ITEMS_SELECTOR}[data-section-id]`);

  if (sourceCartItems instanceof HTMLElement && sourceCartItems.dataset.sectionId) {
    ids.add(sourceCartItems.dataset.sectionId);
  }

  for (const element of root.querySelectorAll(CART_DRAWER_ITEMS_SELECTOR)) {
    if (!(element instanceof HTMLElement) || !element.dataset.sectionId) continue;
    ids.add(element.dataset.sectionId);
  }

  return [...ids].slice(0, CART_BUNDLED_SECTION_LIMIT);
}

function getSectionRoot(sectionId) {
  if (!sectionId) return null;

  try {
    return document.getElementById(buildSectionSelector(sectionId));
  } catch {
    return null;
  }
}

function getCartItemsPatchSelector(sectionId) {
  return `${CART_ITEMS_SELECTOR}[data-section-id="${cssEscape(sectionId)}"]`;
}

function syncDrawerStateFromCartItems(target, source) {
  if (!(target instanceof HTMLElement) || !(source instanceof HTMLElement)) return;

  const drawer = target.closest('cart-drawer');
  const dialog = drawer?.querySelector('dialog');
  if (!(dialog instanceof HTMLElement)) return;

  const isEmpty = source.dataset.fitCartEmpty;
  if (isEmpty === 'true' || isEmpty === 'false') {
    target.dataset.fitCartEmpty = isEmpty;
    dialog.classList.toggle('cart-drawer--empty', isEmpty === 'true');
  }
}

export function patchCartSectionMarkup(sectionId, markup, options = {}) {
  if (!sectionId || !markup) return null;

  const sectionRoot = getSectionRoot(sectionId);
  if (sectionRoot) {
    updateSection(sectionId, markup);
    return sectionRoot;
  }

  const root = options.root && typeof options.root.querySelector === 'function' ? options.root : document;
  const selector = getCartItemsPatchSelector(sectionId);
  const target = root.querySelector(selector);
  if (!(target instanceof HTMLElement)) {
    throw new Error(`Cart section ${sectionId} not found`);
  }

  const source = findHTMLNode(markup, selector);
  if (!(source instanceof HTMLElement)) {
    throw new Error(`Cart section ${sectionId} missing cart-items target`);
  }

  patchHTML(target, markup, source);
  syncDrawerStateFromCartItems(target, source);
  return target;
}

export function getLiveCartItemCount(root = document) {
  const countElement = root.querySelector(CART_COUNT_SELECTOR);
  if (!(countElement instanceof HTMLElement)) return null;

  const dataCount = toCount(countElement.dataset.count);
  if (dataCount != null) return dataCount;

  return toCount(countElement.textContent);
}

export function getCartItemCountFromSections(sections) {
  if (!sections || typeof sections !== 'object') return null;

  for (const markup of Object.values(sections)) {
    if (typeof markup !== 'string' || markup.trim() === '') continue;

    const doc = parseHTMLDocument(markup);
    const count = getLiveCartItemCount(doc);
    if (count != null) return count;
  }

  return null;
}

export function getCartSectionMarkup(source, sectionId) {
  if (!sectionId) return null;

  const sections = getCartSections(source);
  const markup = sections?.[sectionId];

  return typeof markup === 'string' && markup.trim() ? markup : null;
}

export function getCartSectionDocument(source, sectionId) {
  const markup = getCartSectionMarkup(source, sectionId);
  return markup ? parseHTMLDocument(markup) : null;
}

export function resolveCartSectionsUrl() {
  return `${window.location.pathname}${window.location.search}`;
}

function getCartSectionsUrlOption(options = {}) {
  return Object.prototype.hasOwnProperty.call(options, 'sectionsUrl')
    ? options.sectionsUrl
    : resolveCartSectionsUrl();
}

export function buildCartSectionsPayload(payload = {}, options = {}) {
  const next = isPlainObject(payload) ? { ...payload } : {};
  const sectionIds = Array.from(
    new Set((options.sectionIds || collectCartSectionIds(options.root || document)).filter(Boolean)),
  );
  const sectionsUrl = getCartSectionsUrlOption(options);

  if (sectionIds.length > 0) {
    next.sections = sectionIds.join(',');
  } else {
    delete next.sections;
  }

  if (sectionsUrl) {
    next.sections_url = sectionsUrl;
  } else {
    delete next.sections_url;
  }

  return next;
}

export function buildCartAddFormData(source, options = {}) {
  const body = cloneFormData(source);
  const quantity = toPositiveInt(
    options.quantity ?? getSourceQuantityValue(source) ?? body.get('quantity'),
    toPositiveInt(options.quantityDefault, 1) || 1,
  );
  const variantId = asString(options.variantId ?? body.get('id'));
  const sectionIds = Array.from(new Set((options.sectionIds || collectCartSectionIds(options.root || document)).filter(Boolean)));
  const sectionsUrl = getCartSectionsUrlOption(options);

  body.set('quantity', String(quantity));

  if (variantId) {
    body.set('id', variantId);
  }

  if (sectionIds.length > 0) {
    body.set('sections', sectionIds.join(','));
  } else {
    body.delete('sections');
  }

  if (sectionsUrl) {
    body.set('sections_url', sectionsUrl);
  } else {
    body.delete('sections_url');
  }

  return body;
}

function formDataValueToString(value) {
  if (typeof File !== 'undefined' && value instanceof File) return value.name || '';
  return value == null ? '' : String(value);
}

function parseLineItemPropertyName(name) {
  const match = /^properties\[(.+)\]$/.exec(String(name || ''));
  return match ? match[1] : null;
}

function buildCartAddItem(source, options = {}) {
  const body = cloneFormData(source);
  const quantity = toPositiveInt(
    options.quantity ?? getSourceQuantityValue(source) ?? body.get('quantity'),
    toPositiveInt(options.quantityDefault, 1) || 1,
  );
  const variantId = asString(options.variantId ?? body.get('id'));
  const sellingPlan = asString(options.sellingPlan ?? body.get('selling_plan'));
  const item = {
    id: variantId,
    quantity,
  };
  const properties = {};

  if (sellingPlan) {
    item.selling_plan = sellingPlan;
  }

  for (const [name, value] of body.entries()) {
    const propertyName = parseLineItemPropertyName(name);
    if (!propertyName) continue;

    const propertyValue = formDataValueToString(value);
    if (propertyValue === '') continue;
    properties[propertyName] = propertyValue;
  }

  if (Object.keys(properties).length > 0) {
    item.properties = properties;
  }

  return item;
}

export function buildCartAddPayload(source, options = {}) {
  const item = buildCartAddItem(source, options);
  const sectionIds = Array.from(new Set((options.sectionIds || collectCartSectionIds(options.root || document)).filter(Boolean)));
  const sectionsUrl = getCartSectionsUrlOption(options);
  const payload = {
    items: [item],
  };

  if (sectionIds.length > 0) {
    payload.sections = sectionIds.join(',');
  }

  if (sectionsUrl) {
    payload.sections_url = sectionsUrl;
  }

  return payload;
}

function resolveItemCount(source, quantity, root = document) {
  const payloadCount = getPayloadItemCount(source);
  if (payloadCount != null) return payloadCount;

  const sections = getCartSections(source);
  const sectionCount = getCartItemCountFromSections(sections);
  if (sectionCount != null) return sectionCount;

  const liveCount = getLiveCartItemCount(root);
  return liveCount == null ? quantity : liveCount + quantity;
}

function resolveAddItemCount(source, quantity, root = document, baseItemCount = null) {
  const payloadCount = getPayloadItemCount(source);
  if (payloadCount != null) {
    return { count: payloadCount, estimated: false };
  }

  const sections = getCartSections(source);
  const sectionCount = getCartItemCountFromSections(sections);
  if (sectionCount != null) {
    return { count: sectionCount, estimated: false };
  }

  const liveCount = baseItemCount ?? getLiveCartItemCount(root);
  if (liveCount != null) {
    return { count: liveCount + quantity, estimated: true };
  }

  return { count: quantity, estimated: true };
}

export function resolveCartAddEndpoint() {
  const routes = resolveFitCheckGlobal()?.routes || {};
  if (routes.cart_add_url) return routes.cart_add_url;

  const root = routes.root_url || window.Shopify?.routes?.root;
  if (typeof root === 'string' && root.trim()) return root.replace(/\/?$/, '/') + 'cart/add.js';

  return '/cart/add.js';
}

export function resolveCartStateEndpoint() {
  const routes = resolveFitCheckGlobal()?.routes || {};
  const root = routes.root_url || window.Shopify?.routes?.root;
  const cartUrl =
    routes.cart_url ||
    (typeof root === 'string' && root.trim() ? root.replace(/\/?$/, '/') + 'cart' : '/cart');
  return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
}

async function fetchCartStateItemCount(options = {}) {
  try {
    const response = await fetch(options.endpoint || resolveCartStateEndpoint(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
      signal: options.signal,
    });
    if (!response.ok) return null;

    const payload = await response.json();
    return getPayloadItemCount(payload);
  } catch {
    return null;
  }
}

function shouldVerifySuccessfulAddCount(itemCountDetails, quantity, initialItemCount) {
  if (!itemCountDetails || quantity <= 0) return false;

  // In the theme editor iframe, section HTML returned by /cart/add.js can lag
  // behind the mutation. Treat a successful add that resolves below the
  // minimum possible post-add count as stale and verify against /cart.js.
  return itemCountDetails.count < initialItemCount + 1;
}

export async function submitCartSectionsMutation(endpoint, payload = {}, options = {}) {
  const requestPayload = buildCartSectionsPayload(payload, options);
  const performanceAction = typeof options.performanceAction === 'string' ? options.performanceAction : null;
  const requestMarker = performanceAction ? cartPerformance.createStartingMarker(`${performanceAction}:request`) : null;
  let response;

  try {
    response = await fetch(endpoint, {
      ...fetchConfig('json', {
        body: JSON.stringify(requestPayload),
        headers: options.headers,
      }),
      signal: options.signal,
    });
  } finally {
    cartPerformance.measureFromMarker(requestMarker);
  }

  let data = null;
  const parseMarker = performanceAction ? cartPerformance.createStartingMarker(`${performanceAction}:response-parse`) : null;
  try {
    data = await response.json();
  } catch {
    throw new Error('Cart response JSON parse failed');
  } finally {
    cartPerformance.measureFromMarker(parseMarker);
  }

  if (!response.ok) {
    const message = data?.description || data?.message || `Cart request failed (HTTP ${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  const sections = getCartSections(data);

  return {
    payload: isPlainObject(data) ? data : {},
    sections,
    sectionMarkup: options.sectionId ? getCartSectionMarkup(sections, options.sectionId) : null,
    sectionDocument: options.sectionId ? getCartSectionDocument(sections, options.sectionId) : null,
    itemCount: resolveItemCount(data, 0, options.root || document),
  };
}

export async function submitCartAdd(form, options = {}) {
  const root = options.root || document;
  const requestPayload = buildCartAddPayload(form, options);
  const requestItem = requestPayload.items?.[0] || {};
  const quantity = toPositiveInt(requestItem.quantity, toPositiveInt(options.quantityDefault, 1) || 1);
  const variantId = asString(requestItem.id);
  const productId = asString(options.productId);
  const cartAddOperationId = asString(options.cartAddOperationId);
  const initialItemCount = getLiveCartItemCount(root) ?? 0;
  const cartCountSyncTarget = form instanceof EventTarget ? form : document;
  const cartCountSyncOperationId =
    options.optimisticCount !== false && quantity > 0 ? createCartCountSyncOperationId() : null;
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };
  const performanceAction = typeof options.performanceAction === 'string' ? options.performanceAction : 'add';

  if (cartCountSyncOperationId) {
    emitCartCountSync(cartCountSyncTarget, {
      phase: 'start',
      operationId: cartCountSyncOperationId,
      delta: quantity,
      count: initialItemCount,
      source: options.source || null,
    });
  }

  try {
    const requestMarker = cartPerformance.createStartingMarker(`${performanceAction}:request`);
    let response;
    try {
      response = await fetch(options.endpoint || resolveCartAddEndpoint(), {
        ...fetchConfig('json', {
          body: JSON.stringify(requestPayload),
          headers,
        }),
        signal: options.signal,
      });
    } finally {
      cartPerformance.measureFromMarker(requestMarker);
    }

    const parseMarker = cartPerformance.createStartingMarker(`${performanceAction}:response-parse`);
    let parsedPayload;
    try {
      parsedPayload = await parseCartAddPayload(response);
    } finally {
      cartPerformance.measureFromMarker(parseMarker);
    }

    const { payload, rawText } = parsedPayload;

    if (!response.ok) {
      const verifyMarker = cartPerformance.createStartingMarker(`${performanceAction}:cart-state-request`);
      let verifiedCount = null;
      try {
        verifiedCount = await fetchCartStateItemCount({ signal: options.signal });
      } finally {
        cartPerformance.measureFromMarker(verifyMarker);
      }
      const itemCount = verifiedCount ?? initialItemCount;
      const quantityAdded = verifiedCount == null ? 0 : Math.max(itemCount - initialItemCount, 0);

      if (cartCountSyncOperationId) {
        emitCartCountSync(cartCountSyncTarget, {
          phase: 'finish',
          operationId: cartCountSyncOperationId,
          count: itemCount,
          source: options.source || null,
          didError: true,
        });
      }

      return {
        ok: false,
        aborted: false,
        payload: null,
        error: normalizeCartAddFailure(response, payload, rawText),
        eventData: {
          action: 'add',
          cartAddOperationId,
          source: options.source || null,
          itemCount,
          itemCountVerified: verifiedCount != null,
          productId,
          quantityAdded,
          quantityRequested: quantity,
          variantId,
          sections: null,
        },
      };
    }

    const successPayload = payload && typeof payload === 'object' ? payload : {};
    const sections = successPayload.sections && typeof successPayload.sections === 'object' ? successPayload.sections : null;
    let itemCountDetails = resolveAddItemCount(successPayload, quantity, root, initialItemCount);
    let verifiedCount = null;
    if (shouldVerifySuccessfulAddCount(itemCountDetails, quantity, initialItemCount)) {
      const verifyMarker = cartPerformance.createStartingMarker(`${performanceAction}:cart-state-request`);
      try {
        verifiedCount = await fetchCartStateItemCount({ signal: options.signal });
      } finally {
        cartPerformance.measureFromMarker(verifyMarker);
      }
    }
    if (verifiedCount != null) {
      itemCountDetails = { count: verifiedCount, estimated: false, verified: true };
    }

    const itemCount = itemCountDetails.count;
    const eventSections = itemCountDetails.verified ? null : sections;
    const quantityAdded = itemCountDetails.estimated ? null : Math.max(itemCount - initialItemCount, 0);
    const eventData = {
      action: 'add',
      cartAddOperationId,
      source: options.source || null,
      itemCount,
      itemCountEstimated: itemCountDetails.estimated,
      itemCountVerified: itemCountDetails.verified === true,
      productId: productId || asString(successPayload.product_id ?? successPayload.items?.[0]?.product_id),
      quantityAdded,
      quantityRequested: quantity,
      variantId,
      sections: eventSections,
    };

    if (Number.isFinite(quantityAdded) && quantity > 0 && quantityAdded === 0) {
      if (cartCountSyncOperationId) {
        emitCartCountSync(cartCountSyncTarget, {
          phase: 'finish',
          operationId: cartCountSyncOperationId,
          count: itemCount,
          source: options.source || null,
          didError: true,
        });
      }

      return {
        ok: false,
        aborted: false,
        payload: null,
        error: normalizeCartAddNoQuantityFailure(successPayload),
        eventData,
      };
    }

    if (cartCountSyncOperationId) {
      emitCartCountSync(cartCountSyncTarget, {
        phase: 'finish',
        operationId: cartCountSyncOperationId,
        count: itemCount,
        source: options.source || null,
      });
    }

    return {
      ok: true,
      aborted: false,
      payload: successPayload,
      error: null,
      eventData,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      if (cartCountSyncOperationId) {
        emitCartCountSync(cartCountSyncTarget, {
          phase: 'finish',
          operationId: cartCountSyncOperationId,
          count: initialItemCount,
          source: options.source || null,
          didError: true,
        });
      }

      return {
        ok: false,
        aborted: true,
        payload: null,
        error: null,
        eventData: {
          action: 'add',
          cartAddOperationId,
          source: options.source || null,
          itemCount: initialItemCount,
          productId,
          quantityAdded: 0,
          quantityRequested: quantity,
          variantId,
          sections: null,
        },
      };
    }

    if (cartCountSyncOperationId) {
      emitCartCountSync(cartCountSyncTarget, {
        phase: 'finish',
        operationId: cartCountSyncOperationId,
        count: initialItemCount,
        source: options.source || null,
        didError: true,
      });
    }

    return {
      ok: false,
      aborted: false,
      payload: null,
      error: normalizeRuntimeCartAddFailure(error),
      eventData: {
        action: 'add',
        cartAddOperationId,
        source: options.source || null,
        itemCount: initialItemCount,
        productId,
        quantityAdded: 0,
        quantityRequested: quantity,
        variantId,
        sections: null,
      },
    };
  }
}

const fitCheckGlobal = resolveFitCheckGlobal();
if (fitCheckGlobal) {
  fitCheckGlobal.cart = {
    ...(fitCheckGlobal.cart || {}),
    buildCartAddFormData,
    buildCartAddPayload,
    buildCartSectionsPayload,
    collectCartAddSectionIds,
    collectCartSectionIds,
    getCartLineQuantity,
    getCartItemCountFromSections,
    getCartSectionDocument,
    getCartSectionMarkup,
    getCartSections,
    getLiveCartItemCount,
    getQuantityAddedAdjustedMessage,
    getQuantityAdjustedMessage,
    resolveCartSectionsUrl,
    resolveCartStateEndpoint,
    submitCartAdd,
    submitCartSectionsMutation,
  };
}
